import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/auth'
import api from '../lib/api'
import toast from 'react-hot-toast'

export default function SettingsPage() {
  const { user, loadUser } = useAuthStore()
  
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [apiKeyName, setApiKeyName] = useState('')
  
  // Fetch API key info
  const { data: apiKeyInfo, refetch: refetchApiKey } = useQuery({
    queryKey: ['api-key'],
    queryFn: async () => {
      try {
        const res = await api.get('/auth/api-key')
        return res.data
      } catch (err: any) {
        if (err.response?.status === 404) {
          return null // No API key exists
        }
        throw err
      }
    },
  })
  
  const updateProfileMutation = useMutation({
    mutationFn: async () => {
      await api.patch('/auth/me', { full_name: fullName })
    },
    onSuccess: () => {
      toast.success('Profile updated')
      loadUser()
    },
    onError: () => {
      toast.error('Failed to update profile')
    },
  })
  
  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
    },
    onSuccess: () => {
      toast.success('Password changed')
      setCurrentPassword('')
      setNewPassword('')
    },
    onError: () => {
      toast.error('Failed to change password')
    },
  })
  
  const createApiKeyMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/auth/api-key', {
        name: apiKeyName || undefined,
      })
      return res.data
    },
    onSuccess: (data) => {
      // Show the full key (only time it's visible)
      const fullKey = data.key
      navigator.clipboard.writeText(fullKey)
      toast.success('API key created and copied to clipboard!')
      setShowApiKey(true)
      setApiKeyName('')
      refetchApiKey()
      
      // Store the full key temporarily to display
      setTimeout(() => setShowApiKey(false), 30000) // Hide after 30s
    },
    onError: () => {
      toast.error('Failed to create API key')
    },
  })
  
  const deleteApiKeyMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/auth/api-key')
    },
    onSuccess: () => {
      toast.success('API key deleted')
      setShowApiKey(false)
      refetchApiKey()
    },
    onError: () => {
      toast.error('Failed to delete API key')
    },
  })
  
  const handleCopyApiKey = () => {
    if (createApiKeyMutation.data?.key) {
      navigator.clipboard.writeText(createApiKeyMutation.data.key)
      toast.success('API key copied to clipboard')
    }
  }
  
  const handleDeleteApiKey = () => {
    if (confirm('Are you sure? This will revoke the API key immediately and break any integrations using it.')) {
      deleteApiKeyMutation.mutate()
    }
  }
  
  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your account settings
        </p>
      </div>
      
      {/* Profile */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input bg-gray-50"
            />
          </div>
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Role</label>
            <input
              type="text"
              value={user?.role || ''}
              disabled
              className="input bg-gray-50"
            />
          </div>
          <button
            onClick={() => updateProfileMutation.mutate()}
            disabled={updateProfileMutation.isPending}
            className="btn-primary"
          >
            {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      
      {/* Change password */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Change Password</h2>
        <div className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input"
              minLength={8}
            />
            <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
          </div>
          <button
            onClick={() => changePasswordMutation.mutate()}
            disabled={!currentPassword || !newPassword || changePasswordMutation.isPending}
            className="btn-primary"
          >
            {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>
      
      {/* API Key for Webhooks */}
      <div className="card p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">API Key</h2>
        <p className="text-sm text-gray-600 mb-4">
          Use this key for webhook integrations (Zapier, Make, etc.). It never expires until you delete it.
        </p>
        
        {apiKeyInfo ? (
          <div className="space-y-4">
            <div>
              <label className="label">Key Preview</label>
              <input
                type="text"
                value={apiKeyInfo.prefix}
                disabled
                className="input bg-gray-50 font-mono text-sm"
              />
            </div>
            
            {apiKeyInfo.name && (
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={apiKeyInfo.name}
                  disabled
                  className="input bg-gray-50"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Created:</span>{' '}
                <span className="text-gray-900">
                  {new Date(apiKeyInfo.created_at).toLocaleDateString()}
                </span>
              </div>
              {apiKeyInfo.last_used_at && (
                <div>
                  <span className="text-gray-600">Last used:</span>{' '}
                  <span className="text-gray-900">
                    {new Date(apiKeyInfo.last_used_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
            
            {/* Show full key if just created */}
            {showApiKey && createApiKeyMutation.data?.key && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-blue-900">⚠️ Save this key now!</p>
                    <p className="text-xs text-blue-700">You won't be able to see it again.</p>
                  </div>
                  <button
                    onClick={handleCopyApiKey}
                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Copy
                  </button>
                </div>
                <input
                  type="text"
                  value={createApiKeyMutation.data.key}
                  readOnly
                  className="input font-mono text-xs bg-white"
                />
              </div>
            )}
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (confirm('Generate a new API key? The old key will stop working immediately.')) {
                    createApiKeyMutation.mutate()
                  }
                }}
                disabled={createApiKeyMutation.isPending}
                className="btn-secondary"
              >
                {createApiKeyMutation.isPending ? 'Regenerating...' : 'Regenerate Key'}
              </button>
              <button
                onClick={handleDeleteApiKey}
                disabled={deleteApiKeyMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-300 rounded-lg"
              >
                {deleteApiKeyMutation.isPending ? 'Deleting...' : 'Delete Key'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label">Name (optional)</label>
              <input
                type="text"
                value={apiKeyName}
                onChange={(e) => setApiKeyName(e.target.value)}
                placeholder="e.g., Zapier integration"
                className="input"
              />
            </div>
            <button
              onClick={() => createApiKeyMutation.mutate()}
              disabled={createApiKeyMutation.isPending}
              className="btn-primary"
            >
              {createApiKeyMutation.isPending ? 'Generating...' : 'Generate API Key'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
